/**
 * Tiny modal confirming an upload overwrite.
 *
 * Standard Dialog primitive — destructive verb on the right, escape /
 * outside click cancels. The only place colour appears is the
 * destructive button copy via `text-error`; the surface itself stays
 * monochrome per ADR-057.
 */
import { AlertTriangle } from 'lucide-react';

import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

interface Props {
  filename: string;
  folder: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmOverwriteDialog({ filename, folder, onConfirm, onCancel }: Props) {
  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-warning" aria-hidden />
            <DialogTitle>Replace file?</DialogTitle>
          </div>
          <DialogDescription>
            <span className="font-mono text-foreground">{filename}</span> already exists in{' '}
            <span className="font-mono text-foreground">{folder}</span>. The current file will be
            replaced.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onConfirm} className="text-error">
            Replace
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
